"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button, GlassCard, Input } from "@/components/ui";
import { Wordmark } from "@/components/shell";
import { Link } from "@/i18n/navigation";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { forgotPassword, ApiError } from "@/lib/api";
import { useTurnstile } from "@/lib/use-turnstile";

export function ForgotPasswordForm() {
  const t = useTranslations("forgotPassword");
  const tCommon = useTranslations("common");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const turnstile = useTurnstile();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPassword(email.trim(), turnstile.token ?? undefined);
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === "TURNSTILE_FAILED") {
        turnstile.reset();
        setError(t("errors.turnstileFailed"));
      } else if (err instanceof ApiError && err.code === "RATE_LIMITED") {
        setError(t("errors.rateLimited"));
      } else {
        setError(tCommon("error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Wordmark size="lg" href="/" />
        </div>

        <GlassCard padding="lg">
          {sent ? (
            <>
              <h1 className="font-display text-2xl font-semibold text-white">
                {t("sentTitle")}
              </h1>
              <p className="mt-2 text-sm text-white/55">
                {t("sentBody")}
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl font-semibold text-white">
                {t("title")}
              </h1>
              <p className="mt-1 text-sm text-white/55">
                {t("subtitle")}
              </p>

              <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
                <Input
                  label={t("emailLabel")}
                  type="email"
                  autoComplete="email"
                  required
                  placeholder={t("emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                {turnstile.enabled && turnstile.siteKey && (
                  <TurnstileWidget
                    key={turnstile.resetKey}
                    siteKey={turnstile.siteKey}
                    onToken={turnstile.setToken}
                  />
                )}

                {error && (
                  <p
                    role="alert"
                    className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger-light"
                  >
                    {error}
                  </p>
                )}

                <Button type="submit" fullWidth size="lg" isLoading={submitting}>
                  {t("submit")}
                </Button>
              </form>
            </>
          )}
        </GlassCard>

        <p className="mt-6 text-center text-sm text-white/50">
          {t("remembered")}{" "}
          <Link href="/login" className="font-medium text-flamingo hover:text-flamingo-light">
            {t("signIn")}
          </Link>
        </p>
      </div>
    </div>
  );
}
